import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'

import type { ModalSubscribeState } from 'Pages/RecipientsList/model'
import type { PageStateType } from 'Types/state'

import { listsState } from 'Pages/RecipientsList/recoil/state/common'

import { modalSubscribeState } from 'Pages/RecipientsList/recoil/state/modalSubscribe'
import { panelEditRecipientState } from 'Pages/RecipientsList/recoil/state/recipient'

import { fetchDefaultConfirmationMesssage } from 'Pages/RecipientsList/recoil/services/asyncSelectConfirmationMessages'
import { fetchLists } from 'Pages/RecipientsList/recoil/services/common'
import {
  getRecipientSubscriptionLists,
  subscribeMultilist,
} from 'Pages/RecipientsList/recoil/services/modalSubscribe'
import { notifySubscribeMultilistSuccess } from 'Pages/RecipientsList/recoil/services/notifications'

import { reloadRecipientCallback } from 'Pages/RecipientsList/recoil/callbacks/recipient'

import { isNullOrEmpty } from 'Helpers/strings'
import { errorUpdater, loaderUpdater, pageState } from 'State/page'

export const modalSubscribeOpenCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    const { recipient } = await snapshot.getPromise(panelEditRecipientState)

    const { loaded } = await snapshot.getPromise(listsState)

    if (recipient !== undefined) {
      set<PageStateType>(pageState, loaderUpdater(true))

      try {
        if (!loaded) {
          const lists = await fetchLists()

          set(listsState, (prev) => ({
            ...prev,
            items: lists,
            loaded: true,
          }))
        }

        const [idConfirmationMessage, recipientSubscriptionLists] =
          await Promise.all([
            fetchDefaultConfirmationMesssage(),
            getRecipientSubscriptionLists(recipient),
          ])

        set(modalSubscribeState, (prev) => ({
          ...prev,
          show: true,
          idRecipient: recipient.Id,
          contactEmail: recipient.Email,
          contactSMS: recipient.Mobile,

          recipientSubscriptionLists,
          lists: [],

          channelEmail: !isNullOrEmpty(recipient.Email),
          channelSMS: !isNullOrEmpty(recipient.Mobile),

          sendConfirmation: false,
          forceSubscription: false,
          idConfirmationMessage,
        }))
      } catch (error) {
        console.error('### error', error)

        logAppError({
          Component: 'RecipientsList',
          Method: 'modalSubscribeOpenCallback',
          appError: error,
        })

        set<PageStateType>(pageState, errorUpdater)
      }

      set<PageStateType>(pageState, loaderUpdater(false))
    }
  }

export const modalSubscribeCancelCallback =
  ({ set, reset }: CallbackInterface) =>
  async () => {
    //

    reset(modalSubscribeState)
  }

export const modalSubscribeConfirmCallback =
  ({ set, reset, snapshot, ...cbi }: CallbackInterface) =>
  async () => {
    set<PageStateType>(pageState, loaderUpdater(true))

    const { contactEmail, contactSMS, ...subscription } =
      await snapshot.getPromise(modalSubscribeState)

    const { items: availLists } = await snapshot.getPromise(listsState)

    try {
      await subscribeMultilist({ ...subscription, availLists })

      await reloadRecipientCallback({ set, reset, snapshot, ...cbi })(
        subscription.idRecipient,
      )

      notifySubscribeMultilistSuccess(contactEmail, contactSMS)
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalSubscribeConfirmCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    reset(modalSubscribeState)

    set<PageStateType>(pageState, loaderUpdater(false))
  }

//

export const modalSubscribeChangeCallback =
  ({ set }: CallbackInterface) =>
  async (p: Partial<ModalSubscribeState>) => {
    set(modalSubscribeState, (prev) => ({
      ...prev,
      ...p,
    }))
  }

export const modalSubscribeToggleFieldCallback =
  ({ set }: CallbackInterface) =>
  async (
    p:
      | 'allLists'
      | 'channelEmail'
      | 'channelSMS'
      | 'sendConfirmation'
      | 'forceSubscription',
  ) => {
    set(modalSubscribeState, (prev) => ({
      ...prev,
      [p]: !prev[p],
    }))
  }
